import type { BookingPreferences, SelectedSlot } from "../bookingFlowMachine"
import { BookedAppointment, ScreeningProgram, useBookAppointment, useBookingOffering } from "../hooks"
import { appointmentTypeCopy, FOLLOW_UP_HELPER_TEXT, offeringSummaryLine } from "../preferencesCopy"
import { AppointmentAvailability } from "./AppointmentAvailability"
import { VisitTypeCard } from "./VisitTypeCard"
import { UsaStates } from "@internal/constants"
import {
  Alert,
  Box,
  Breadcrumb,
  Breadcrumbs,
  Button,
  Divider,
  Progress,
  Typography
} from "@carrotfertility/carotene-core"
import {
  Form,
  FormAutocomplete,
  FormRadioGroup,
  type FormSelectOption,
  useFormContext,
  useWatch
} from "@carrotfertility/carotene-core-x"
import React from "react"
import { useIntl } from "react-intl"

type SelectPreferencesPageProps = {
  program: ScreeningProgram
  onConfirmBooking: (preferences: BookingPreferences, slot: SelectedSlot, appointment: BookedAppointment) => void
  onReturnToProviderResults?: () => void
}

type PreferencesFormValues = {
  appointmentTypeId: string
  // FormAutocomplete stores the selected option object (or null), not a bare value.
  region: FormSelectOption | null
  // Set by AppointmentAvailability once the member picks a date + time.
  selectedDate: string | null
  selectedSlot: SelectedSlot | null
}

// The screen's own copy is English literal (see preferencesCopy.ts); the location-of-care
// options are localized here from the shared @internal/constants state list.
export function SelectPreferencesPage({
  program,
  onConfirmBooking,
  onReturnToProviderResults
}: SelectPreferencesPageProps): JSX.Element {
  const intl = useIntl()
  const offeringQuery = useBookingOffering(program)
  const bookAppointment = useBookAppointment()

  if (offeringQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Progress />
      </Box>
    )
  }

  if (offeringQuery.isError || !offeringQuery.data) {
    return <Alert severity="error">Something went wrong loading your options. Please try again.</Alert>
  }

  const offering = offeringQuery.data
  const appointmentTypes = offering.appointmentTypes
  // The summary line and default selection follow the primary bookable type (the initial
  // consult on a first visit).
  const primaryType = appointmentTypes.find((type) => type.bookable) ?? appointmentTypes[0]

  const regionOptions = UsaStates.map((state) => ({ value: state.id, label: intl.formatMessage(state.label) }))

  const handleSubmit = async (values: PreferencesFormValues) => {
    // The Confirm button is disabled until a slot is chosen; guard anyway.
    if (!values.selectedSlot) return
    const slot = values.selectedSlot
    try {
      // Place the booking, then advance — the confirmation screen needs the returned
      // appointment (assigned provider + visit window). A failure surfaces inline (see
      // AppointmentAvailability) and leaves the member here to retry.
      const appointment = await bookAppointment.mutateAsync({
        program,
        slotToken: slot.slotToken,
        // The member doesn't pick a provider; take the first eligible host for the slot.
        hostId: slot.availableHostIds[0]
      })
      onConfirmBooking(
        {
          appointmentTypeId: values.appointmentTypeId,
          region: values.region ? String(values.region.value) : ""
        },
        slot,
        appointment
      )
    } catch {
      // Booking failed; bookAppointment.isError drives the inline error + retry.
    }
  }

  return (
    <Form<PreferencesFormValues>
      onSubmit={handleSubmit}
      defaultValues={{ appointmentTypeId: primaryType?.id ?? "", region: null, selectedDate: null, selectedSlot: null }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.lg),
          maxInlineSize: 640
        })}
      >
        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Breadcrumbs>
            {onReturnToProviderResults ? (
              <Breadcrumb component="button" type="button" onClick={onReturnToProviderResults}>
                Provider results
              </Breadcrumb>
            ) : (
              <Breadcrumb>Provider results</Breadcrumb>
            )}
            <Breadcrumb>Health questions</Breadcrumb>
            <Breadcrumb currentPage>See availability</Breadcrumb>
          </Breadcrumbs>
          <Divider />
        </Box>

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xxs) })}
        >
          <Typography variant="h1">Select your preferences to see available appointment times.</Typography>
          {primaryType && (
            <Typography color="text.secondary">{offeringSummaryLine(offering, primaryType.durationMinutes)}</Typography>
          )}
        </Box>

        <FormRadioGroup
          name="appointmentTypeId"
          label="What kind of help do you need?"
          controllerProps={{ rules: { required: "Please select a visit type." } }}
          helperText={FOLLOW_UP_HELPER_TEXT}
          radioGroupProps={{
            row: true,
            sx: (theme) => ({
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: theme.spacing(theme.tokens.spacing.sm)
            })
          }}
          options={appointmentTypes.map((type) => ({
            value: type.id,
            disabled: !type.bookable,
            label: appointmentTypeCopy(type.kind).title
          }))}
          renderItem={({ radioProps }) => {
            const type = appointmentTypes.find((candidate) => candidate.id === radioProps.value)
            return type ? <VisitTypeCard key={type.id} type={type} radioProps={radioProps} /> : null
          }}
        />

        <Box sx={{ maxInlineSize: 440 }}>
          <FormAutocomplete
            name="region"
            label="Location of care"
            controllerProps={{ rules: { required: "Please select your location of care." } }}
            fullWidth
            options={regionOptions}
          />
        </Box>

        <Divider />

        <AvailabilityStep
          program={program}
          booking={{ isPending: bookAppointment.isLoading, isError: bookAppointment.isError }}
        />
      </Box>
    </Form>
  )
}

// Two-phase, on one screen: the member first submits their visit type + location ("See
// availability"), which reveals the date + time picker (AppointmentAvailability owns that and
// the "Confirm booking"/"Continue" CTA). Lives inside <Form> so it can validate + read the
// preference fields. Editing the visit type or location after revealing hides availability
// again — the shown slots were for the old preferences.
function AvailabilityStep({
  program,
  booking
}: {
  program: ScreeningProgram
  booking: { isPending: boolean; isError: boolean }
}): JSX.Element {
  const { trigger, getValues, setValue } = useFormContext<PreferencesFormValues>()
  const appointmentTypeId = useWatch<PreferencesFormValues>({ name: "appointmentTypeId" })
  const region = useWatch<PreferencesFormValues>({ name: "region" })
  const stateCode = region ? String(region.value) : ""

  // The preferences the member submitted to reveal availability; null until they do.
  const [submitted, setSubmitted] = React.useState<{ appointmentTypeId: string; stateCode: string } | null>(null)

  React.useEffect(() => {
    if (submitted && (submitted.appointmentTypeId !== appointmentTypeId || submitted.stateCode !== stateCode)) {
      setSubmitted(null)
      setValue("selectedDate", null)
      setValue("selectedSlot", null)
    }
  }, [appointmentTypeId, stateCode, submitted, setValue])

  const onSeeAvailability = async () => {
    // Submitting the preferences: validate the required fields, then reveal availability.
    const valid = await trigger(["appointmentTypeId", "region"])
    if (!valid) return
    const values = getValues()
    setSubmitted({
      appointmentTypeId: values.appointmentTypeId,
      stateCode: values.region ? String(values.region.value) : ""
    })
  }

  if (!submitted) {
    return (
      <Box>
        <Button type="button" color="secondary" onClick={onSeeAvailability}>
          See availability
        </Button>
      </Box>
    )
  }

  return (
    <AppointmentAvailability
      program={program}
      appointmentTypeId={submitted.appointmentTypeId}
      stateCode={submitted.stateCode}
      booking={booking}
    />
  )
}
